import { useState, useEffect, useRef } from "react";
import { Check, X } from "lucide-react";
import { AxiosError } from "axios";
// import apiClient from "../../config/api";
import type {
  StyleNumberValidation,
  DjangoStyleNumberVerificationData,
} from "../../types";
import { useGeneralApiCall } from "@/services/useGeneralApiCall";

interface StyleNumberInputProps {
  value: string;
  onChange: (value: string) => void;
}

export const StyleNumberInput = ({
  value,
  onChange,
}: StyleNumberInputProps) => {
  const [validation, setValidation] = useState<StyleNumberValidation | null>(
    null
  );
  const [isChecking, setIsChecking] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const { getApi } = useGeneralApiCall();

  const validateStyleNumber = async (styleNumber: string) => {
    if (!styleNumber.trim()) {
      setValidation(null);
      return;
    }

    try {
      setIsChecking(true);
      // Use Django API endpoint for style number verification
      //   const response = await apiClient.get<DjangoStyleNumberVerificationData>(
      //     `/style/v1/verify-style-number/${encodeURIComponent(styleNumber)}/`
      //   );

      const response = await getApi<DjangoStyleNumberVerificationData>(
        `api/style/v1/verify-style-number/${encodeURIComponent(styleNumber)}/`
      );

      const { exists, message } = response.data;

      setValidation({
        isValid: !exists,
        exists,
        message:
          message ||
          (exists
            ? "Style number already exists"
            : "Style number is available"),
      });
    } catch (error) {
      console.error("Error validating style number:", error);

      // Check if it's an Axios error and if it's a 404 (style number doesn't exist - which means it's available)
      if (error instanceof Error && "response" in error) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 404) {
          setValidation({
            isValid: true,
            exists: false,
            message: "Style number is available",
          });
          return;
        }
      }

      setValidation({
        isValid: false,
        exists: false,
        message: "Style Number Already Exists.",
      });
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      validateStyleNumber(value);
    }, 500);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setValidation(null); // Clear validation while typing
  };

  return (
    <div className="form-group">
      {/* <label htmlFor="style-number">Style Number</label> */}
      <div className="input-with-validation">
        <input
          type="text"
          id="style-number"
          value={value}
          onChange={handleInputChange}
          placeholder="Enter style number"
          className={`table-input ${
            validation ? (validation.isValid ? "valid" : "invalid") : ""
          }`}
        />

        {isChecking && (
          <div className="validation-indicator checking">
            <span className="loading-spinner-small">...</span>
            <span>Checking availability...</span>
          </div>
        )}

        {validation && !isChecking && (
          <div
            className={`validation-indicator ${
              validation.isValid ? "valid" : "invalid"
            }`}
          >
            {validation.isValid ? (
              <Check size={16} className="validation-icon success" />
            ) : (
              <X size={16} className="validation-icon error" />
            )}
            <span>{validation.message}</span>
          </div>
        )}
      </div>
    </div>
  );
};
